import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  BackgroundCheckResponse,
  BackgroundCheckUploadRequest,
  CreatePaymentIntentResponse,
  InitiateBackgroundCheckRequest,
  ProfileCompletionResponse
} from '@umss/shared/data-access';

@Injectable({ providedIn: 'root' })
export class BackgroundCheckService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/contractors/me/background-check';

  initiate(
    request: InitiateBackgroundCheckRequest
  ): Observable<BackgroundCheckResponse> {
    return this.http.post<BackgroundCheckResponse>(
      `${this.baseUrl}/checkr`,
      request
    );
  }

  getStatus(): Observable<BackgroundCheckResponse> {
    return this.http.get<BackgroundCheckResponse>(`${this.baseUrl}/status`);
  }

  /**
   * Step 1 of the upfront payment flow — creates a Stripe PaymentIntent on the
   * backend and returns the clientSecret for Stripe Elements to confirm.
   */
  createPaymentIntent(): Observable<CreatePaymentIntentResponse> {
    return this.http.post<CreatePaymentIntentResponse>(
      `${this.baseUrl}/payment-intent`,
      {}
    );
  }

  /**
   * Reads server-side profile completion. Source of truth for whether the
   * contractor may start the deferred (paycheck) flow.
   */
  getProfileCompletion(): Observable<ProfileCompletionResponse> {
    return this.http.get<ProfileCompletionResponse>(
      '/api/contractors/me/profile-completion'
    );
  }

  /**
   * Uploads an existing background check document.
   * Reads the file as base64 and POSTs JSON — same pattern as contractor
   * document uploads on the rest of the platform.
   *
   * BE endpoint: POST /api/contractors/me/background-check/upload
   * Response: BackgroundCheckResponse with Status=Completed, Outcome=Clear,
   * Provider="ContractorUpload" — no admin review step.
   */
  uploadDocument(
    file: File,
    expirationDate?: string
  ): Observable<BackgroundCheckResponse> {
    return new Observable<BackgroundCheckResponse>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        const body: BackgroundCheckUploadRequest = {
          fileName: file.name,
          fileContent: base64,
          mimeType: file.type || 'application/pdf',
          expirationDate: expirationDate ?? null
        };
        this.http
          .post<BackgroundCheckResponse>(`${this.baseUrl}/upload`, body)
          .subscribe(observer);
      };
      reader.onerror = () => observer.error(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
